import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AccountCircle,
  Lock as LockIcon,
} from '@mui/icons-material';
import { authService } from '../services/authService';
import { formatRUT, validateRUT } from '../utils/validators';
import { LoginCredentials } from '../types';
import PasswordManager from '../utils/passwordManager';
import PrimaryButton from '../components/PrimaryButton';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<LoginCredentials>();

  const handleRUTChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatRUT(e.target.value);
    setValue('username', formatted);
  };

  const onSubmit = async (data: LoginCredentials) => {
    setError('');
    setLoading(true);
    
    try {
      await authService.login(data);
      // Removido localStorage del usuario por seguridad
      
      // Guardar la contraseña para uso en información de emergencia (10 minutos)
      PasswordManager.setTemporaryPassword(data.password, 600000);
      
      navigate('/dashboard');
    } catch (err: any) {
      // Manejar errores de validación 422 de FastAPI
      if (err.response?.status === 422 && err.response?.data?.detail) {
        const validationErrors = err.response.data.detail;
        if (Array.isArray(validationErrors)) {
          // Formatear errores de validación de Pydantic
          const errorMessages = validationErrors.map((error: any) => {
            const field = error.loc?.join(' -> ') || 'Campo';
            return `${field}: ${error.msg}`;
          }).join(', ');
          setError(errorMessages);
        } else {
          setError(validationErrors);
        }
      } else {
        // Manejar otros tipos de errores
        setError(err.response?.data?.detail || t('auth.login.errors.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <LockIcon sx={{ color: 'white', fontSize: 30 }} />
          </Box>
          
          <Typography component="h1" variant="h5" fontWeight="bold" mb={3}>
            {t('auth.login.title')}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label={t('auth.login.rut')}
              placeholder="12.345.678-9"
              margin="normal"
              {...register('username', {
                required: t('auth.login.errors.rutRequired'),
                validate: (value) => validateRUT(value) || t('auth.login.errors.rutInvalid'),
              })}
              onChange={handleRUTChange}
              error={!!errors.username}
              helperText={errors.username?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label={t('auth.login.password')}
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              {...register('password', {
                required: t('auth.login.errors.passwordRequired'),
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <PrimaryButton
              type="submit"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                fontSize: '1.1rem',
              }}
            >
              {loading ? <CircularProgress size={24} /> : t('auth.login.button')}
            </PrimaryButton>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('auth.login.noAccount')}{' '}
                <Link to="/register" style={{ color: '#1976d2', textDecoration: 'none' }}>
                  {t('auth.login.register')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('auth.login.secure')}
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default Login; 